import React, { useEffect, useMemo, useState } from 'react';
import { Inbox } from 'lucide-react';
import { Sidebar, NavKey } from '../components/Sidebar';
import { Toolbar } from '../components/Toolbar';
import { BoardView } from '../components/BoardView';
import { ListView } from '../components/ListView';
import { IssueDetailPanel } from '../components/IssueDetailPanel';
import { CreateIssueModal } from '../components/CreateIssueModal';
import { useIssueStore, CURRENT_USER_ID } from '../store';
import { useScreenInit } from '../useScreenInit.js';
const NAV_TITLES: Record<NavKey, string> = {
  inbox: 'Inbox',
  'my-issues': 'My Issues',
  views: 'All Issues'
};
export function Tracker() {
  const screenInit = useScreenInit();
  const {
    issues,
    addIssue,
    updateIssue
  } = useIssueStore();
  const [view, setView] = useState<'board' | 'list'>(screenInit?.view === 'list' ? 'list' : 'board');
  const [nav, setNav] = useState<NavKey>(screenInit?.nav as NavKey || 'views');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const selectedIssue = issues.find(i => i.id === selectedIssueId) || null;
  const filteredIssues = useMemo(() => {
    switch (nav) {
      case 'my-issues':
        return issues.filter(i => i.assigneeId === CURRENT_USER_ID);
      case 'inbox':
        // Recent activity: everything, most recently updated first.
        return [...issues].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case 'views':
      default:
        return issues;
    }
  }, [issues, nav]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for search placeholder
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // Search logic would go here
      }
      // 'c' to create issue when not typing in an input
      if (e.key === 'c' && !e.metaKey && !e.ctrlKey) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setIsCreateModalOpen(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  return <div className="flex h-screen w-full bg-white overflow-hidden font-sans text-slate-900" data-id="element-211">
      <Sidebar onNewIssue={() => setIsCreateModalOpen(true)} activeNav={nav} onNavChange={setNav} data-id="element-212" />

      <div className="flex-1 flex flex-col min-w-0 relative" data-id="element-213">
        <Toolbar view={view} setView={setView} issueCount={filteredIssues.length} title={NAV_TITLES[nav]} data-id="element-214" />

        {filteredIssues.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center bg-white text-center px-6" data-id="element-215">
            <div className="w-10 h-10 rounded-full bg-[#f7f8f9] border border-[#eef0f2] flex items-center justify-center mb-3" data-id="element-216">
              <Inbox className="w-5 h-5 text-slate-400" data-id="element-217" />
            </div>
            <h2 className="text-[15px] font-medium text-slate-900" data-id="element-218">
              {nav === 'my-issues' ? 'No issues assigned to you' : 'No issues here yet'}
            </h2>
            <p className="text-[13px] text-slate-500 mt-1 max-w-xs" data-id="element-219">
              {nav === 'my-issues' ? 'Issues assigned to you will show up here.' : 'Create a new issue to get started.'}
            </p>
            <button onClick={() => setIsCreateModalOpen(true)} className="mt-4 px-3 py-1.5 bg-[#5e6ad2] text-white text-[13px] font-medium rounded hover:bg-[#4f5ab8] transition-colors shadow-sm" data-id="element-220">
              New Issue
            </button>
          </div> : view === 'board' ? <BoardView issues={filteredIssues} onIssueClick={issue => setSelectedIssueId(issue.id)} onUpdateIssueStatus={(id, status) => updateIssue(id, {
        status
      })} data-id="element-221" /> : <ListView issues={filteredIssues} onIssueClick={issue => setSelectedIssueId(issue.id)} data-id="element-222" />}
      </div>

      <IssueDetailPanel issue={selectedIssue} onClose={() => setSelectedIssueId(null)} onUpdate={updateIssue} data-id="element-223" />

      <CreateIssueModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onCreate={addIssue} data-id="element-224" />
    </div>;
}